import { Inject, Injectable, isDevMode } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { catchError, from, interval, of, startWith, tap } from 'rxjs';

export interface LogEntry {
  level: 'log' | 'info' | 'warn' | 'error' | 'debug';
  message: any[];
  timestamp: string;
  url: string;
  stack?: string;
}

@Injectable({
  providedIn: 'root',
})
export class UserLoggerService {
  private buffer: LogEntry[] = [];
  private batchSize = 20;
  private batchInterval = 10000; // flush logs every 10s
  private debugCheckInterval = 30000; // check synchronizer backend every 30s
  private logTimer: any;
  private debugTimer: any;
  private debugEnabled = false;
  private isFlushing = false;


  endpoint = '/synchronizerService/v1/logs';

  constructor(private http: HttpClient, @Inject(OKTA_AUTH) private oktaAuth: OktaAuth) {
    // 🟢 start periodic debug check every 30s
    this.startDebugCheck();
  }

  /** Periodically check if debug logging is enabled for this user */
private startDebugCheck(): void {
  interval(this.debugCheckInterval)
    .pipe(startWith(0))
    .subscribe(() => this.checkDebugStatus());
}

  private checkDebugStatus(): void {
    const token = this.oktaAuth.getAccessToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const url = `${this.endpoint}/enabled`;

    this.http.get<boolean>(url, { headers }).subscribe({
      next: (res: boolean) => {
        const newStatus = !!res;

        if (newStatus !== this.debugEnabled) {
          this.debugEnabled = newStatus;
          console.info(`Debug status changed: ${newStatus}`);

          // (Re)start or stop the log flush timer
          if (this.debugEnabled) {
            if (!this.logTimer) {
              this.logTimer = setInterval(() => this.flush(), this.batchInterval);
            }
          } else {
            if (this.logTimer) {
              clearInterval(this.logTimer);
              this.logTimer = null;
            }
          }
        }
      },
      error: (err) => {
        console.warn('Failed to check debug status:', err);
      }
    });
  }


  private push(level: LogEntry['level'], ...args: any[]): void {
    if (!this.debugEnabled) return; // ignore if debug not enabled

    const entry: LogEntry = {
      level,
      message: args.map(a => (typeof a === 'object' ? JSON.stringify(a) : String(a))),
      timestamp: new Date().toISOString(),
      url: window.location.href,
      stack: new Error().stack?.split('\n').slice(2, 6).join(' | ')
    };

    this.buffer.push(entry);
    if (this.buffer.length >= this.batchSize) {
      this.flush();
    }
  }

  /** --- Flush batched logs to backend --- **/
  flush(): void {
  if (this.isFlushing || !this.debugEnabled || this.buffer.length === 0) return;
    this.isFlushing = true;

  const currentBuffer = [...this.buffer];
  this.buffer = [];

  const authToken = this.oktaAuth.getAccessToken();
  const data = JSON.stringify(currentBuffer);
  const headers = {
    'Content-Type': 'application/json',
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
  };


  this.http.post(this.endpoint, data, { headers }).pipe(
    tap(() => { this.isFlushing = false; }),
    catchError(err => {
      console.warn('⚠️ Log flush failed:', err);
      this.isFlushing = false;
      return of(null);
    })
  ).subscribe();
}


  /** --- Public logging methods --- **/
  log(...args: any[]): void { this.push('log', ...args); console.log(...args); }
  info(...args: any[]): void { this.push('info', ...args); console.info(...args); }
  warn(...args: any[]): void { this.push('warn', ...args); console.warn(...args); }
  error(...args: any[]): void { this.push('error', ...args); console.error(...args); }
  debug(...args: any[]): void { this.push('debug', ...args); if (isDevMode()) console.debug(...args); }




  getLogs(): LogEntry[] { return [...this.buffer]; }

  capture(level: 'log' | 'info' | 'warn' | 'error' | 'debug', ...args: any[]) {
    try {
      const entry: LogEntry = {
        level, message: args.map(a => (typeof a === 'object' ? JSON.stringify(a) : String(a))), timestamp: new Date().toISOString(), url: window.location.href,
        stack: new Error().stack?.split('\n').slice(2, 6).join(' | ')
      }; this.buffer.push(entry);
      if (this.buffer.length >= this.batchSize) { this.flush(); }
    }
    catch (e) { // si un bug de log se produit, on ne veut pas casser l’app 
    }
  }
}
